import { Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { saveAs } from 'file-saver-es';
import { isNil } from 'lodash-es';
import { from, map, switchMap } from 'rxjs';

import { PIPELINE_TYPE } from '@app/modules/assets/models/pipeline-builder.model';
import { CodeMirrorComponent } from '@app/shared/components/code-mirror/code-mirror.component';
import { DialogBoxComponent } from '@app/shared/components/dialog-box/dialog-box.component';
import type { Item } from '@app/shared/components/registry/action-btns/action-btns.component';
import { FEATURE_TYPE, OBJECT_DISPLAY_NAMES, OBJECTS } from '@app/shared/constants/shared.constant';
import type { JobModel } from '@app/shared/models/job.model';
import type { WithExportableService } from '@app/shared/services/mixins/export.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';

@Component({
    selector: 'cor-download-options',
    templateUrl: './download-options.component.html',
    styleUrl: './download-options.componenet.scss',
    imports: [
        ReactiveFormsModule,
        DialogBoxComponent,
        CodeMirrorComponent,
        FormsModule,
        ReactiveFormsModule,
        CheckboxModule,
        TooltipModule,
        ButtonModule,
    ],
})
export class DownloadOptionsComponent {
    readonly object = input<OBJECTS>();

    readonly id = input<number>();

    readonly item = input<Item>();

    readonly service = input<Partial<WithExportableService>>();

    readonly job = input<JobModel>();

    protected OBJECT_DISPLAY_NAMES = OBJECT_DISPLAY_NAMES;

    protected readonly downloadInProgress = signal(false);

    private readonly destroyRef = inject(DestroyRef);
    private readonly toast = inject(ToastNotificationService);
    protected readonly pythonCode = {
        code: signal<string>(null),
        isShowCodeVisible: signal(false),
        commentDecorators: signal(true),
        isExporting: signal(false),
    };

    // Example code shown to user to run the code
    protected readonly examplePythonCode = computed(() => {
        if (!this.item()?.alias) return null;

        const alias = this.item()?.alias;

        const mandatoryArgs = this.item()
            ?.arguments.filter(arg => !arg.isOptional)
            .map(arg => `${arg.alias}=...`);
        const optionalArgs = this.item()
            ?.arguments.filter(arg => arg.isOptional)
            .map(arg => {
                const addQuotes = (val: string) => `'${val.replace(/'/g, "\\'")}'`;
                if (arg.inputType === FEATURE_TYPE.STRING) {
                    return `${arg.alias}=${isNil(arg.defaultValue) ? 'None' : addQuotes(arg.defaultValue as string)}`;
                }
                return `${arg.alias}=${isNil(arg.defaultValue) ? 'None' : (arg.defaultValue as string)}`;
            });

        let chatArgs: string[] = [];
        if (
            this.object() === OBJECTS.PIPELINE &&
            this.item()?.pipelineType === PIPELINE_TYPE.CHAT
        ) {
            chatArgs = ['user_message=...', 'history=...', 'context=...'];
        } else if (
            this.object() === OBJECTS.PIPELINE &&
            this.item()?.pipelineType === PIPELINE_TYPE.SUMMARIZATION
        ) {
            chatArgs = ['document=...'];
        }

        return `${alias}(${[...chatArgs, ...mandatoryArgs, ...optionalArgs].join(', ')})`;
    });

    // Visible code is what is shown to the user based on their selections of commentDecorators or not.
    protected readonly visibleCode = computed(() =>
        this.pythonCode.commentDecorators()
            ? this.pythonCode.code().replace(/^# \[strip\] /gm, '# ')
            : this.pythonCode.code().replace(/^# \[strip\] /gm, ''),
    );

    protected onExportDocument() {
        this.downloadInProgress.set(true);

        this.service()
            .exportDocument(this.id())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: fileData => {
                    const header = fileData.headers.get('content-disposition');
                    const fileName = header.split(';')[1].split('=')[1].replace(/"/g, '');
                    saveAs(fileData.body, fileName);
                    this.toast.showSuccess('Download Finished', '', { timeOut: 20000 });
                },
                error: () => this.toast.showError('Exporting Documentation failed'),
            })
            .add(() => this.downloadInProgress.set(false));
    }

    protected onExportCode(mouseEvent: MouseEvent) {
        // Stop the mouse event so that the dropdown remains open - this way user can see `Exporting...`
        // while the export is in progress
        mouseEvent.preventDefault();
        mouseEvent.stopPropagation();

        this.downloadInProgress.set(true);

        this.service()
            .exportPython(this.id())
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(response => from(response.body.text())),
            )
            .subscribe({
                next: content => {
                    this.pythonCode.code.set(content);
                    this.pythonCode.isShowCodeVisible.set(true);
                },
                error: () => this.toast.showError('Exporting Code failed'),
            })
            .add(() => this.downloadInProgress.set(false));
    }

    downloadCodeAsZip() {
        this.pythonCode.isExporting.set(true);
        this.service()
            .exportPython(this.id(), {
                asZip: true,
                asFolder: true,
                commentDecorators: this.pythonCode.commentDecorators(),
            })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                map(response => {
                    this.toast.showSuccess('Download finished', '', {
                        timeOut: 20000,
                    });
                    return response;
                }),
            )
            .subscribe(data => {
                // Parsing Content-Disposition taken from: https://stackoverflow.com/a/63030106
                const header = data.headers.get('content-disposition');
                saveAs(data.body, header.split(';')[1].split('=')[1].replace(/"/g, ''));
            })
            .add(() => this.pythonCode.isExporting.set(false));
    }
}
